// ── MoneylineSection — drops the canonical value-dashboard GameRows
//    into GameDetail so the row + expandable ladder behavior is
//    pixel-identical instead of a near-clone ─────────────────────────
//
// Reuses `GameRows` exported from ValueDashboard/GameTable.tsx as the
// row renderer. That component already handles:
//   • Click-row-to-expand (no chevron affordance)
//   • Pill-click stops propagation and forwards to onTradeClick
//   • Inline ladder with 3 ASKS → per-venue last-trade strip → 3 BIDS
//   • marketSide-tinted team-name badge when expanded
//   • PosRing position/order chips
//   • American odds + size on every ladder level
//
// Section-local toolbar (Maker/Taker/Market + Yes/No chips) controls
// `marketSide` + `priceMode` for THIS table only — independent from
// the value dashboard's global toolbar. Defaults: Yes/No = NO, price
// mode = user's displaySettings default.
//
// Trade-click → seeds GameDetail's right-column trade ticket via
// onPillClick. We map the canonical GameRows signature
//   (gameKey, teamSide, venue, opts) → TradeTicketContext
// inline.

import { useEffect, useMemo, useState } from 'react';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useMarketStore } from '../../stores/marketStore';
import { useUserKalshiPOS } from '../../hooks/useUserKalshiPOS';
import { useGameStatesPoll } from '../../hooks/useGameStatesPoll';
import { toHydraGameKey } from '../../services/analyticsGameKey';
import { GameRows } from '../ValueDashboard/GameTable';
import { SectionToolbar } from './SectionToolbar';
import type { GameRow, MarketSide, PriceMode, TeamSide } from '../ValueDashboard/types';
import type { TradeTicketContext } from '../ValueDashboard/TradeTicketBody';
import type { OddsStreamGameState } from '../../services/oddsStream';

interface Props {
  game: GameRow;
  /** Soccer 3-way: the Draw outcome as a singleSide synth GameRow
   *  (multiOutcomeDrawRow). Renders as a continuation row under the
   *  away/home pair — same layout as the 1H Tie in Game Lines. Absent
   *  (binary sports) the section renders exactly as before. */
  drawGame?: GameRow | null;
  /** Optional deep-link hint from the Positions page. If this ticker
   *  matches one of the four moneyline books, the matching team's row
   *  expands automatically on mount. */
  focusTicker?: string | null;
  onPillClick: (ctx: TradeTicketContext) => void;
  /** Live game state from the page's WS odds stream. Passed straight to
   *  GameRows so the moneyline score tracks the header (which reads the
   *  same stream) instead of the laggier analytics-REST store. */
  liveGameState?: OddsStreamGameState | null;
  /** This section is showing the knockout "To Advance" 2-way headline (not the
   *  1X2). Routes the Odds column to the advance consensus (a different market
   *  from the 90-min result it shares a canonicalGameKey with). */
  advanceConsensus?: boolean;
}

const VIEW_ID_PREFIX = 'game-detail-moneyline';

/** Expansion target: a pair side, or the Draw continuation row. */
type ExpandedRow = TeamSide | 'draw' | null;

export function MoneylineSection({ game, drawGame = null, focusTicker, onPillClick, liveGameState, advanceConsensus = false }: Props) {
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  const setOrderbookNeeds = useMarketStore((s) => s.setOrderbookNeeds);

  // Pre-expand the team whose moneyline ticker matches the URL deep-link
  // ticker (positions page → click). Computed once via initializer so
  // user-driven toggles after mount aren't overridden by re-renders.
  const initialExpandedSide = useMemo<ExpandedRow>(() => {
    if (!focusTicker) return null;
    if (game.kalshi?.away.marketTicker === focusTicker) return 'away';
    if (game.kalshi?.home.marketTicker === focusTicker) return 'home';
    if (game.polymarket?.away.marketTicker === focusTicker) return 'away';
    if (game.polymarket?.home.marketTicker === focusTicker) return 'home';
    if (drawGame?.kalshi?.away.marketTicker === focusTicker) return 'draw';
    if (drawGame?.polymarket?.away.marketTicker === focusTicker) return 'draw';
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Section-local toolbar state. Defaults: NO + user's default price mode.
  const [marketSide, setMarketSide] = useState<MarketSide>('no');
  const [priceMode, setPriceMode] = useState<PriceMode>(defaultPriceMode);
  const [expandedRow, setExpandedRow] = useState<ExpandedRow>(initialExpandedSide);
  const expandedTeamSide: TeamSide | null =
    expandedRow === 'away' || expandedRow === 'home' ? expandedRow : null;

  // Kalshi-only ticker list so the user-activity hook can query queue
  // positions for the moneyline tickers. Poly orders flow through a
  // separate relay-poll so they don't need to be in this set.
  const kalshiTickers = useMemo(() => {
    const t: string[] = [];
    if (game.kalshi?.away.marketTicker) t.push(game.kalshi.away.marketTicker);
    if (game.kalshi?.home.marketTicker) t.push(game.kalshi.home.marketTicker);
    if (drawGame?.kalshi?.away.marketTicker) t.push(drawGame.kalshi.away.marketTicker);
    return t;
  }, [game, drawGame]);
  const { positionByTicker, ordersByTicker } = useUserKalshiPOS(kalshiTickers);

  // GameDetail isn't behind the dashboard's gameStates poll, so a cold
  // deep-link here left liveGameState null and the Score column fell back to
  // the scheduled start time on an in-progress game. Poll this one game's
  // state so the live/final score (and period status) populate like they do
  // on the dashboard. Writes to the shared gameStatesStore GameRows reads.
  const gameStateKeys = useMemo(() => {
    const k = toHydraGameKey(game.key);
    return k ? [k] : [];
  }, [game.key]);
  useGameStatesPoll(gameStateKeys);

  // Hydra streams depth only for declared tickers. Subscribe to both
  // K + P books for the moneyline so the ladder paints immediately on
  // first click without waiting for an extra subscribe round-trip.
  const allTickers = useMemo<string[]>(() => {
    const out: string[] = [];
    if (game.kalshi?.away.marketTicker) out.push(game.kalshi.away.marketTicker);
    if (game.kalshi?.home.marketTicker) out.push(game.kalshi.home.marketTicker);
    if (game.polymarket?.away.marketTicker) out.push(game.polymarket.away.marketTicker);
    if (game.polymarket?.home.marketTicker) out.push(game.polymarket.home.marketTicker);
    if (drawGame?.kalshi?.away.marketTicker) out.push(drawGame.kalshi.away.marketTicker);
    if (drawGame?.polymarket?.away.marketTicker) out.push(drawGame.polymarket.away.marketTicker);
    return out;
  }, [game, drawGame]);
  useEffect(() => {
    const viewId = `${VIEW_ID_PREFIX}:${game.key}`;
    setOrderbookNeeds(viewId, allTickers);
    return () => {
      setOrderbookNeeds(viewId, []);
    };
  }, [game.key, allTickers, setOrderbookNeeds]);

  const handleTradeClick = (
    gameKey: string,
    teamSide: TeamSide,
    venue: 'kalshi' | 'polymarket',
    opts?: { priceCents?: number; initialOrderSide?: 'yes' | 'no'; postOnly?: boolean },
  ) => {
    // Draw-row clicks carry the draw synth (teamSide is always 'away' on a
    // singleSide row); the parent maps either game to the right outcome.
    const isDraw = drawGame != null && gameKey === drawGame.key;
    onPillClick({
      game: isDraw ? drawGame : game,
      teamSide: isDraw ? 'away' : teamSide,
      venue,
      initialOrderSide: opts?.initialOrderSide ?? marketSide,
      prefillPrice: opts?.priceCents,
      postOnly: opts?.postOnly ?? (priceMode === 'maker'),
    });
    // Flip the table's YES/NO toggle to match the pill the trader
    // just clicked — table + panel then read consistently from the
    // toolbar instead of asking for a separate toggle.
    if (opts?.initialOrderSide && opts.initialOrderSide !== marketSide) {
      setMarketSide(opts.initialOrderSide);
    }
  };

  return (
    <section className="bg-card border-border rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-foreground text-lg font-semibold">Moneyline</h2>
        <SectionToolbar
          priceMode={priceMode}
          onPriceMode={setPriceMode}
          marketSide={marketSide}
          onMarketSide={setMarketSide}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            {/* Column set must match what the shared GameRows emits for
                hideDateColumn (Date dropped, everything else present).
                GameRows split the old Score/Odds cell into separate Score
                and Odds columns (Team | Score | Odds | Kalshi | …); this
                hand-rolled header has to track them or every data cell
                renders under the wrong label. */}
            <tr className="text-muted-foreground text-left text-[10px] font-semibold uppercase tracking-wider">
              <th className="w-48 px-3 py-2">Team</th>
              <th
                className="w-28 px-3 py-2 text-center"
                title="Kickoff time + countdown until the game starts, then the live/final score."
              >
                {game.gameState === 'live' || game.gameState === 'final' ? 'Score' : 'Time'}
              </th>
              <th
                className="w-16 px-3 py-2 text-center"
                title="Calculated from unabated market sources"
              >
                Odds
              </th>
              <th className="w-32 px-3 py-2 text-center">Kalshi</th>
              <th className="w-16 px-3 py-2 text-right" title="Total BID/ASK contracts">K Vol</th>
              <th className="w-24 px-3 py-2 text-right" title="Total $ resting in book">K Liq</th>
              <th
                className="w-16 px-3 py-2 text-right"
                title={`Best-bid depth on the active ${marketSide.toUpperCase()} side`}
              >
                K Q
              </th>
              <th
                className="w-16 px-3 py-2 text-right"
                title="Kalshi best-bid depth change over the last 1m on the active side"
              >
                K Δ/m
              </th>
              <th className="w-32 px-3 py-2 text-center">Poly</th>
              <th className="w-16 px-3 py-2 text-right" title="Total BID/ASK contracts">P Vol</th>
              <th className="w-24 px-3 py-2 text-right" title="Total $ resting in book">P Liq</th>
              <th
                className="w-16 px-3 py-2 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side"
              >
                P Δ/m
              </th>
            </tr>
          </thead>
          <tbody>
            <GameRows
              game={game}
              gameIndex={0}
              marketSide={marketSide}
              priceMode={priceMode}
              advanceConsensus={advanceConsensus}
              expandedTeamSide={expandedTeamSide}
              groupSelected={expandedRow === 'draw'}
              groupExtendsBelow={drawGame != null}
              onToggleExpand={(_key, side) =>
                setExpandedRow((prev) => (prev === side ? null : side))
              }
              onTradeClick={handleTradeClick}
              positionByTicker={positionByTicker}
              ordersByTicker={ordersByTicker}
              hideDateColumn
              hideSelectionBox
              showScores
              gameStateOverride={liveGameState}
            />
            {/* Soccer Draw — continuation row under the pair, same as the 1H
                Tie in Game Lines. oddsOutcome="draw" points the Odds column
                at the 3-way drawFairProbCents and blanks the per-team score
                cell (the synth's away slot holds the DRAW market, so the
                away team's goals would print here otherwise). */}
            {drawGame && (
              <GameRows
                game={drawGame}
                gameIndex={0}
                marketSide={marketSide}
                priceMode={priceMode}
                singleSide="away"
                continuation
                oddsOutcome="draw"
                expandedTeamSide={expandedRow === 'draw' ? 'away' : null}
                groupSelected={expandedTeamSide !== null}
                onToggleExpand={() =>
                  setExpandedRow((prev) => (prev === 'draw' ? null : 'draw'))
                }
                onTradeClick={handleTradeClick}
                positionByTicker={positionByTicker}
                ordersByTicker={ordersByTicker}
                hideDateColumn
                hideSelectionBox
                showScores
                gameStateOverride={liveGameState}
              />
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
